import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Tax Service - API calls for VAT and profit tax management
 */
export const taxService = {
    /**
     * Calculate VAT for a single order
     */
    async calculateOrderVat(orderId) {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/vat/calculate/${orderId}`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate order VAT');
            return data;
        } catch (error) {
            console.error('Error calculating order VAT:', error);
            throw error;
        }
    },

    /**
     * Calculate VAT for all orders in a period
     */
    async calculatePeriodVat(startDate, endDate) {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/vat/calculate-period`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ start_date: startDate, end_date: endDate })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate period VAT');
            return data;
        } catch (error) {
            console.error('Error calculating period VAT:', error);
            throw error;
        }
    },

    /**
     * Get monthly VAT report (for filing)
     */
    async getMonthlyVatReport(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);

            const response = await fetch(`${API_BASE_URL}/taxes/vat/report/monthly?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get monthly VAT report');
            return data;
        } catch (error) {
            console.error('Error getting monthly VAT report:', error);
            throw error;
        }
    },

    /**
     * Get profit tax estimate for a period
     */
    async getProfitTaxEstimate(year, quarter = null) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (quarter) params.append('quarter', quarter);

            const response = await fetch(`${API_BASE_URL}/taxes/profit/estimate?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get profit tax estimate');
            return data;
        } catch (error) {
            console.error('Error getting profit tax estimate:', error);
            throw error;
        }
    },

    /**
     * Get annual tax summary
     */
    async getAnnualTaxSummary(year) {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/annual-summary?year=${year}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get annual tax summary');
            return data;
        } catch (error) {
            console.error('Error getting annual tax summary:', error);
            throw error;
        }
    },

    /**
     * Quick VAT calculation helper
     */
    async calculateVat(amount, vatRate = 14, isInclusive = true) {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/calculate-vat`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    amount,
                    rate: vatRate,
                    pricing_type: isInclusive ? 'inclusive' : 'exclusive'
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate VAT');
            return data;
        } catch (error) {
            console.error('Error calculating VAT:', error);
            throw error;
        }
    },

    /**
     * Get tax configuration
     */
    async getConfig() {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/config`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get tax config');
            return data;
        } catch (error) {
            console.error('Error getting tax config:', error);
            throw error;
        }
    },

    /**
     * Update tax configuration
     */
    async updateConfig(configData) {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/config`, {
                method: 'PUT',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(configData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update tax config');
            return data;
        } catch (error) {
            console.error('Error updating tax config:', error);
            throw error;
        }
    },

    /**
     * Get default tax configuration
     */
    async getDefaultConfig() {
        try {
            const response = await fetch(`${API_BASE_URL}/taxes/config/defaults`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get default config');
            return data;
        } catch (error) {
            console.error('Error getting default config:', error);
            throw error;
        }
    }
};
